import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import {
    AppShell,
    useComputedColorScheme,
    useMantineTheme
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useGetApiDocumentsId } from '../api/documents/documents.ts';
import { DocumentDto } from '../api/models';
import { useGetApiProjects } from '../api/projects/projects.ts';
import { Footer } from './Footer';
import HeaderComponent from './Header/Header.tsx';
import Navbar from './Navbar/Navbar.tsx';

interface SelectedDocumentInfo {
    document: DocumentDto;
    projectName: string;
}

const LayoutContent = () => {
    const theme = useMantineTheme();
    const { documentId } = useParams();

    // If no document is selected in URL, default to expanded navbar
    const [navbarOpened, { toggle: toggleNavbar }] = useDisclosure(!documentId);
    const [selectedDocumentInfo, setSelectedDocumentInfo] = useState<
        SelectedDocumentInfo | undefined
    >();

    // Fetch document and projects data to populate header when navbar is collapsed
    const { data: documentData } = useGetApiDocumentsId(documentId ?? '', {
        query: { enabled: !!documentId }
    });
    const { data: projectsData } = useGetApiProjects({
        query: { enabled: !!documentId && !selectedDocumentInfo }
    });

    const computedColorScheme = useComputedColorScheme('dark');
    const dark = computedColorScheme === 'dark';

    // Set selected document info from direct API calls when navbar is collapsed and we have a documentId
    useEffect(() => {
        if (documentId && documentData && projectsData && !navbarOpened) {
            // Find the project that contains this document
            const project = projectsData.find(p =>
                p.documents.some(d => d.id === documentId)
            );

            if (project) {
                const document = project.documents.find(
                    d => d.id === documentId
                );
                if (
                    document &&
                    selectedDocumentInfo?.document.id !== document.id
                ) {
                    setSelectedDocumentInfo({
                        document,
                        projectName: project.name
                    });
                }
            }
        } else if (!documentId && selectedDocumentInfo) {
            // Clear selected document info when navigating away from document
            setSelectedDocumentInfo(undefined);
        }
    }, [
        documentId,
        documentData,
        projectsData,
        navbarOpened,
        selectedDocumentInfo
    ]);

    return (
        <div>
            <AppShell
                styles={{
                    main: {
                        background: dark
                            ? theme.colors.dark[8]
                            : theme.colors.gray[2],
                        height: '100%'
                    }
                }}
                header={{ height: 50 }}
                footer={{ height: 40 }}
                navbar={{
                    width: navbarOpened ? 380 : 60,
                    breakpoint: 'xs'
                }}
                padding={'xs'}
            >
                {/* low zindex so it doesn't obscure tooltips on editor */}
                <AppShell.Header p='xs' zIndex={'1'}>
                    <HeaderComponent
                        selectedDocumentInfo={selectedDocumentInfo}
                        isNavbarOpen={navbarOpened}
                    />
                </AppShell.Header>
                <AppShell.Main>
                    <Outlet />
                </AppShell.Main>
                <AppShell.Navbar p='xs'>
                    <Navbar
                        isExpanded={navbarOpened}
                        onToggle={toggleNavbar}
                        selectedDocumentInfo={selectedDocumentInfo}
                        onSelectedDocumentInfoChange={setSelectedDocumentInfo}
                    />
                </AppShell.Navbar>
                <AppShell.Footer>
                    <Footer />
                </AppShell.Footer>
            </AppShell>
        </div>
    );
};

export const Layout = () => {
    return <LayoutContent />;
};
